import { useId, type ReactNode } from 'react'
import { Icon } from '@/components/Icon'

/**
 * The input skin, shared with the native `<select>`s the screens use directly.
 * Read-only is keyed on the `[readonly]` attribute, not `:read-only` — that
 * pseudo-class matches a `<select>` as well, and every select rendered grey.
 * The focus ring is a box-shadow, not a thicker border: a border that grows on
 * focus moves every field below it by a pixel.
 */
export const FIELD_CONTROL =
  'w-full min-h-field rounded-md border border-control bg-surface px-3 py-2 text-base leading-snug text-fg transition duration-150 ease-standard placeholder:text-fg-subtle hover:not-disabled:not-[[readonly]]:border-fg-subtle focus:border-accent focus:ring-3 focus:ring-focus/20 focus:outline-none disabled:border-line disabled:bg-subtle disabled:text-fg-disabled [&[readonly]]:cursor-default [&[readonly]]:border-line [&[readonly]]:bg-subtle [&[readonly]]:text-fg-muted'

/** Colour is never the only signal: the field also carries `aria-invalid` and
 *  a message with an icon below it. */
export const FIELD_INVALID =
  'border-danger! shadow-[inset_0_0_0_1px_var(--color-danger)] focus:ring-danger-soft'

interface FieldProps {
  label: ReactNode
  /** Required fields carry a mark; optional ones say nothing. Almost everything
   *  in this app is optional, and "(optional)" nine times is noise. */
  required?: boolean
  error?: string | undefined
  hint?: string | undefined
  children: (props: {
    id: string
    /** For a custom control that is not a labelable element: point its
     *  `aria-labelledby` here. */
    labelId: string
    describedBy: string | undefined
    invalid: boolean
  }) => ReactNode
}

export function Field({ label, required = false, error, hint, children }: FieldProps) {
  const id = useId()
  const labelId = `${id}-label`
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ')

  return (
    <div className="flex min-w-0 flex-col gap-1">
      {/* Always visible, above the field. A placeholder is not a label — it
          vanishes exactly when the user needs it. */}
      <label id={labelId} className="text-xs leading-snug font-medium text-fg-muted" htmlFor={id}>
        {label}
        {required && (
          <span className="text-danger" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      {children({ id, labelId, describedBy: describedBy || undefined, invalid: Boolean(error) })}
      {hint && (
        <span className="text-xs leading-snug text-fg-subtle" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span
          className="flex items-start gap-2 text-xs leading-snug text-danger"
          id={errorId}
          role="alert"
        >
          <Icon name="alert" size="1rem" className="mt-[0.1em]" />
          {error}
        </span>
      )}
    </div>
  )
}
